import {
  LitElement,
  html,
  css,
  unsafeHTML,
  ref,
  createRef,
  utils,
  paths,
  t,
  styles,
} from '@hh/global.js'

class HHSlider extends LitElement {
  static properties = {
    active: {
      reflect: true,
    },
    breakpoint: {
      type: Number,
    },
    _current: {
      state: true,
    },
    _total: {
      state: true,
    },
    shadowstylesheet: {
      type: String,
    },
  }

  $container = createRef()
  $next = createRef()
  $prev = createRef()

  constructor() {
    super()

    this._total = 0
    this._current = 1

    this.updateUI = this.updateUI.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="item"]'))
    this._total = this.items.length

    this.observer = new ResizeObserver((...args) => {
      this.activate(...args)
    })

    setTimeout(() => {
      this.$container?.value?.addEventListener('scroll', this.updateUI)
      if (this.breakpoint) {
        this.observer.observe(document.body)
      }
    }, 0)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.$container?.value?.removeEventListener('scroll', this.updateUI)
    this.observer.disconnect()
  }

  activate([first, ...all]) {
    const { contentRect: { width = 0 } = {} } = first || {}
    if (width < this.breakpoint && !this.active) {
      this.active = true
    } else if (width >= this.breakpoint && this.active) {
      this.active = false
    }
  }

  updateUI() {
    if (!this._total) {
      return
    }

    this._current =
      Math.round(
        this.$container?.value?.scrollLeft / this.items[0].clientWidth
      ) + 1

    this._current === 1
      ? this.$prev?.value?.setAttribute('disabled', true)
      : this.$prev?.value?.removeAttribute('disabled')

    this._current === this._total
      ? this.$next?.value?.setAttribute('disabled', true)
      : this.$next?.value?.removeAttribute('disabled')
  }

  move(event) {
    event.preventDefault()
    const slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.$container?.value?.scrollLeft + this.items[0].clientWidth
        : this.$container?.value?.scrollLeft - this.items[0].clientWidth
    this.$container?.value?.scrollTo({
      left: slideScrollPosition,
    })
  }

  render() {
    return html`
      ${this.shadowstylesheet &&
      unsafeHTML(
        `<link type="text/css" rel="stylesheet" href="${this.shadowstylesheet}">`
      )}
      <div class="wrapper">
        <div class="container" ${ref(this.$container)}>
          <slot name="item"></slot>
        </div>
      </div>
      <div class="buttons">
        <div class="counter">
          <span class="counter--current">${this._current}</span>
          <span aria-hidden="true"> / </span>
          <span class="visually-hidden">${t('accessibility.of', 'of')}</span>
          <span class="counter--total">${this._total ? this._total : ''}</span>
        </div>
        <button
          ${ref(this.$prev)}
          @click="${this.move}"
          type="button"
          class="button button--prev"
          name="previous"
          aria-label="${t('accessibility.previous_slide', 'Previous')}"
          disabled="true"
        >
          <svg
            aria-hidden="true"
            focusable="false"
            role="presentation"
            width="12"
            height="10"
            viewBox="0 0 12 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.89062 0.85791L10.1406 5.10791L5.89062 9.35791"
              stroke="currentColor"
              stroke-width="1.5"
            />
            <path
              d="M9.89062 5.10791H0.390625"
              stroke="currentColor"
              stroke-width="1.5"
            />
          </svg>
        </button>
        <button
          ${ref(this.$next)}
          @click="${this.move}"
          type="button"
          class="button button--next"
          name="next"
          aria-label="${t('accessibility.next_slide', 'Next')}"
        >
          <svg
            aria-hidden="true"
            focusable="false"
            role="presentation"
            width="12"
            height="10"
            viewBox="0 0 12 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.89062 0.85791L10.1406 5.10791L5.89062 9.35791"
              stroke="currentColor"
              stroke-width="1.5"
            />
            <path
              d="M9.89062 5.10791H0.390625"
              stroke="currentColor"
              stroke-width="1.5"
            />
          </svg>
        </button>
      </div>
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      :host([active='true']) {
        position: relative;
      }

      .container {
        display: var(--hh-container-flex);
        flex-wrap: var(--hh-container-flex-wrap);
        margin: var(--hh-container-margin);
      }

      :host([active='true']) .container {
        display: flex;
        flex-wrap: nowrap;
        position: relative;
        background: var(--hh-background);
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scroll-behavior: smooth;
        scroll-padding-left: 1rem;
        -webkit-overflow-scrolling: touch;
      }

      :host([active='true']) ::slotted(*) {
        width: var(--hh-cell-width) !important;
        min-width: var(--hh-cell-width) !important;
        padding-right: var(--hh-cell-spacing, 0);
      }

      .buttons {
        display: none;
        align-items: center;
        position: absolute;
        z-index: 2;
        right: 0;
        bottom: 0;
        border: var(--hh-button-border);
        background: var(--hh-button-background);
      }

      :host([active='true']) .buttons {
        display: flex;
        gap: var(--hh-button-gap, 7px);
      }

      .counter {
        margin: 0 2rem;
        display: var(--hh-counter-display, block);
      }

      .button {
        width: var(--hh-button-width, 36px);
        height: var(--hh-button-height, 36px);
        cursor: pointer;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        border-radius: 100px;
        background-color: var(--hh-button-background);
        color: var(--hh-color);
        box-shadow: 0 4px 4px 0px rgba(0, 0, 0, 0.1);
      }

      .button:hover {
        color: var(--hh-button-background);
        background-color: var(--hh-color);
        border: none;
        border-width: 0;
      }

      .button:disabled,
      .button:disabled:hover {
        cursor: not-allowed;
        border: none;
        color: var(--hh-disabled-color);
        border-color: var(--hh-disabled-color);
        background-color: var(--hh-button-background);
      }

      .button svg {
        height: 0.9rem;
      }

      .button--prev svg {
        transform: rotate3d(0, 1, 0, 180deg);
      }
    `,
  ]
}

customElements.define('hh-slider', HHSlider)

class SliderComponent extends LitElement {
  static properties = {
    active: {
      reflect: true,
    },
    _current: {
      state: true,
    },
    _totalItems: {
      state: true,
    },
  }

  constructor() {
    super()

    this.slider = this.querySelector('.slider__list')
    this.sliderItems = this.slider?.querySelectorAll('.slider__slide')
    this.prevButton = this.querySelector('button[name="previous"]')
    this.nextButton = this.querySelector('button[name="next"]')

    this._totalItems = 0
    this._current = 1

    this.updateUI = this.updateUI.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    if (!this.isiOS) {
      this.slider.classList.add('custom-scrollbar')
    }

    this.prevButton?.addEventListener('click', this.onButtonClick.bind(this))
    this.nextButton?.addEventListener('click', this.onButtonClick.bind(this))

    this.slider?.addEventListener('scroll', this.updateUI, {
      passive: true,
    })

    this._totalItems = this.sliderItems.length

    setTimeout(() => {
      this.updateUI()
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  get isiOS() {
    return [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod',
      'Mac'
    ].includes(navigator.userAgent)
    // iPad on iOS 13 detection
    || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  }

  onButtonClick(event) {
    event.preventDefault()
    if (!this.isiOS) {
      this.slider.classList.add('custom-scrollbar-visible')
    }
    const slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + this.sliderItems[0].clientWidth
        : this.slider.scrollLeft - this.sliderItems[0].clientWidth

    this.slider.scrollTo({
      left: slideScrollPosition,
      behavior: 'smooth',
    })
  }

  updateUI() {
    if (!this._totalItems) return

    this._current =
      Math.round(this.slider.scrollLeft / this.sliderItems[0].clientWidth) + 1

    this.slidesPerPage = Math.floor(
      this.slider.clientWidth / this.sliderItems[0].clientWidth
    )

    this.totalPages = this._totalItems - this.slidesPerPage + 1

    this._current === 1
      ? this.prevButton?.setAttribute('disabled', true)
      : this.prevButton?.removeAttribute('disabled')

    this._current === this.totalPages
      ? this.nextButton?.setAttribute('disabled', true)
      : this.nextButton?.removeAttribute('disabled')

    const progress = this._current / this.totalPages

    this.style.setProperty('--slider-progress', progress)
  }

  render() {
    return html`<slot></slot>`
  }
}

customElements.define('slider-component', SliderComponent)
